import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    PackageSearch, ChevronDown, ChevronRight, Loader2, RefreshCw, Send, Paperclip,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';
import { uploadFileToS3 } from '../../api/commonAPI/awsUploadAPI';
import { S3_FOLDERS } from '../../config/s3Config';

import {
    fetchMultiGrid,
    fetchMultiItemDetails,
    submitMultiVendorReciept,
    clearMultiItemDetails,
    clearMultiSubmitResult,
    resetTransferRecieptMulti,
    selectTRMGridItems,
    selectTRMItemDetails,
    selectTRMSubmitResult,
    selectTRMLoading,
    selectTRMErrors,
} from '../../slices/purchaseSlice/transferRecieptMultiSlice';

// ── Shared UI (mirrors TransferReciept.jsx) ─────────────────────────────────

const inputCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 ' +
    'text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 ' +
    'focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 ' +
    'disabled:opacity-60 disabled:cursor-not-allowed transition-colors';

const Label = ({ children, required }) => (
    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SpinnerIcon = () => <Loader2 className="w-4 h-4 animate-spin" />;

const todayFormatted = () => {
    const d = new Date();
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${String(d.getDate()).padStart(2,'0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
};

const formatDateForSP = (d) => {
    if (!d) return '';
    if (d instanceof Date) {
        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        return `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
    }
    return String(d);
};

const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));

const TransferRecieptMulti = () => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const currentUser = userData?.userName || userData?.employeeId || userData?.UID || 'system';
    const roleId = userData?.roleId || userData?.RID || '';
    const uid = userData?.UID || userData?.uid || '';

    const gridItems    = useSelector(selectTRMGridItems);
    const itemDetails  = useSelector(selectTRMItemDetails);
    const submitResult = useSelector(selectTRMSubmitResult);
    const loading      = useSelector(selectTRMLoading);
    const errors       = useSelector(selectTRMErrors);

    const [expandedRow, setExpandedRow] = useState(null); // the grid row object currently expanded
    const [checkedRows, setCheckedRows] = useState({});
    const [receivedEdits, setReceivedEdits] = useState({});
    const [date, setDate] = useState(todayFormatted());
    const [notes, setNotes] = useState('');
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    useEffect(() => {
        if (roleId && uid) dispatch(fetchMultiGrid({ roleId, uid }));
        return () => dispatch(resetTransferRecieptMulti());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (submitResult) {
            (async () => {
                if (file && submitResult.downloadExt) {
                    setUploading(true);
                    setUploadProgress(0);
                    try {
                        await uploadFileToS3(file, S3_FOLDERS.MRR, submitResult.downloadExt, setUploadProgress);
                    } catch (err) {
                        const detail = err?.ErrorDetails?.ErrorMessage || err?.Message || err?.message || 'Unknown error';
                        toast.warn(`Receipt saved, but the document upload failed (${detail}). Please retry the upload separately.`);
                    } finally {
                        setUploading(false);
                        setUploadProgress(0);
                    }
                }
                toast.success(`Material Reciept No (MR No):-  ${submitResult.mrrNo}  Successfully Submitted`);
                dispatch(clearMultiSubmitResult());
                collapseRow();
                if (roleId && uid) dispatch(fetchMultiGrid({ roleId, uid }));
            })();
        } else if (errors.submit) {
            toast.error(errors.submit);
            dispatch(clearMultiSubmitResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitResult, errors.submit]);

    // ── Row expand / collapse ────────────────────────────────────────────────

    const resetRowState = () => {
        setCheckedRows({}); setReceivedEdits({});
        setDate(todayFormatted()); setNotes(''); setFile(null);
    };

    const collapseRow = () => {
        setExpandedRow(null);
        dispatch(clearMultiItemDetails());
        resetRowState();
    };

    const toggleRow = (row) => {
        if (expandedRow?.Mrrno === row.Mrrno) { collapseRow(); return; }
        setExpandedRow(row);
        resetRowState();
        dispatch(fetchMultiItemDetails({ mrrno: row.Mrrno, pono: row.PoNo, potype: row.POType }));
    };

    // ── Item grid edits ──────────────────────────────────────────────────────

    const toggleChecked = (rid) => setCheckedRows((p) => ({ ...p, [rid]: !p[rid] }));

    // Mirrors legacy's blur validation exactly (TransferRecieptDataMultiGrid.cshtml's
    // ".countcheck3" blur handler): raised must be >= pendingVerification + alreadyReceived +
    // entered, else reject; if raised is strictly greater (or entered is 0) confirm with the user.
    const handleReceivedBlur = (row, value) => {
        if (value === '') {
            toast.error(`Enter Recieved Quantity for ${row.ItemCode}`);
            return;
        }
        const raised = num(row.RaisedQty);
        const alreadyReceived = num(row.RecievedQty);
        const pendingVerification = num(row.PendingVerificationQty);
        const entered = num(value);
        const plusAmount = pendingVerification + alreadyReceived + entered;

        if (raised < plusAmount) {
            toast.error(`Invalid Recieved Quantity for ${row.ItemCode}`);
            setReceivedEdits((p) => ({ ...p, [row.Rid]: '' }));
            return;
        }
        if (raised > plusAmount || entered === 0) {
            const ok = window.confirm(`Do you really want to continue with the quantity for the Item Code:- ${row.ItemCode}?`);
            if (!ok) {
                setReceivedEdits((p) => ({ ...p, [row.Rid]: '' }));
            }
        }
    };

    // ── Submit ───────────────────────────────────────────────────────────────

    const handleSubmit = () => {
        if (!expandedRow) return;

        if (!file) { toast.error('Upload Pdf File'); return; }
        if (file.name.split('.').pop().toLowerCase() !== 'pdf') { toast.error('Invalid Upload File. Upload only PDF File'); return; }
        if (!date) { toast.error('Enter Date'); return; }
        if (!notes.trim()) { toast.error('Enter Notes'); return; }
        if (itemDetails.length === 0) { toast.error('Invalid Submission'); return; }
        if (!itemDetails.every((r) => checkedRows[r.Rid])) { toast.error('Please Verify Item Codes'); return; }
        if (!itemDetails.every((r) => (receivedEdits[r.Rid] ?? '') !== '')) { toast.error('Please Enter Recieved Quantity'); return; }

        const sumAvlQuantity = itemDetails.reduce((sum, r) => sum + num(r.AvlQuantity), 0);
        if (sumAvlQuantity <= 0) {
            toast.error("Avaliable Qty is zero or some PO related MRR's are pending");
            return;
        }

        let ids = '', itemCodes = '', basics = '', receivedQtys = '';
        let sumRaised = 0, sumReceived = 0;
        itemDetails.forEach((r) => {
            const entered = num(receivedEdits[r.Rid]);
            const raised = num(r.RaisedQty);
            ids          += `${r.Rid},`;
            itemCodes    += `${r.ItemCode},`;
            basics       += `${num(r.Basic)},`;
            receivedQtys += `${entered},`;
            sumRaised += raised;
            sumReceived += entered + num(r.RecievedQty); // entered + already-received
        });

        dispatch(submitMultiVendorReciept({
            Rid: ids,
            ItemCodes: itemCodes,
            RecievedQty: receivedQtys,
            Basic: basics,
            Date: formatDateForSP(date),
            Remarks: notes,
            SumRecievedQty: sumReceived,
            SumRaisedQty: sumRaised,
            PoNo: expandedRow.PoNo,
            OldMrrno: expandedRow.Mrrno,
            CreatedBy: currentUser,
            Roleid: String(roleId),
            Filechk: file ? 'Yes' : 'No',
            Extension: file ? file.name.split('.').pop() : '',
        }));
    };

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 space-y-5">

            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow">
                        <PackageSearch className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold text-gray-900 dark:text-white">Multi Transfer Reciept</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Receive the next installment against a multi-receipt vendor PO.</p>
                    </div>
                </div>
                <button onClick={() => roleId && uid && dispatch(fetchMultiGrid({ roleId, uid }))}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors">
                    <RefreshCw className="w-3.5 h-3.5" /> Refresh
                </button>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                {loading.grid ? (
                    <div className="flex items-center justify-center py-16"><SpinnerIcon /></div>
                ) : gridItems.length === 0 ? (
                    <div className="py-16 text-center text-sm text-gray-400 dark:text-gray-500">No Records Found</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/40">
                                    {['', 'Previous MRR No', 'Received From', 'Vendor Name', 'Indent No', 'CC/Job Code', 'MRR Type', 'PO No', 'PO Type'].map((h) => (
                                        <th key={h} className="text-left py-2.5 px-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-700/40">
                                {gridItems.map((row) => {
                                    const open = expandedRow?.Mrrno === row.Mrrno;
                                    return (
                                        <React.Fragment key={row.Mrrno}>
                                            <tr className="hover:bg-gray-50/60 dark:hover:bg-gray-700/20 cursor-pointer" onClick={() => toggleRow(row)}>
                                                <td className="py-2.5 px-3 text-gray-400">
                                                    {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                                </td>
                                                <td className="py-2.5 px-3 font-semibold text-gray-800 dark:text-gray-100 whitespace-nowrap">{row.Mrrno}</td>
                                                <td className="py-2.5 px-3 whitespace-nowrap">{row.Type}</td>
                                                <td className="py-2.5 px-3">{row.VendorName}</td>
                                                <td className="py-2.5 px-3 whitespace-nowrap">{row.IndentNo}</td>
                                                <td className="py-2.5 px-3 whitespace-nowrap">{row.CCCode}</td>
                                                <td className="py-2.5 px-3 whitespace-nowrap">{row.MrrType}</td>
                                                <td className="py-2.5 px-3 whitespace-nowrap">{row.PoNo}</td>
                                                <td className="py-2.5 px-3 whitespace-nowrap">{row.POType}</td>
                                            </tr>
                                            {open && (
                                                <tr>
                                                    <td colSpan={9} className="bg-indigo-50/40 dark:bg-indigo-900/10 p-4">
                                                        {loading.itemDetails ? (
                                                            <div className="flex items-center justify-center py-8"><SpinnerIcon /></div>
                                                        ) : (
                                                            <div className="space-y-4">
                                                                <div className="overflow-x-auto rounded-lg border border-indigo-200 dark:border-indigo-700">
                                                                    <table className="w-full text-xs">
                                                                        <thead>
                                                                            <tr className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white">
                                                                                {['Chk', 'Item Code', 'Item Name', 'Specification', 'DCA', 'Sub DCA', 'Basic', 'Units', 'Amount', 'Raised Qty', 'Already Recieved', 'Pending Verification', 'Available Qty', 'Recieved Qty'].map((h) => (
                                                                                    <th key={h} className="px-2.5 py-2.5 text-left font-semibold whitespace-nowrap">{h}</th>
                                                                                ))}
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {itemDetails.map((r, idx) => (
                                                                                <tr key={r.Rid} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-indigo-50/40 dark:bg-indigo-900/10'}>
                                                                                    <td className="px-2.5 py-2">
                                                                                        <input type="checkbox" className="w-4 h-4 accent-indigo-600 cursor-pointer"
                                                                                            checked={!!checkedRows[r.Rid]} onChange={() => toggleChecked(r.Rid)} />
                                                                                    </td>
                                                                                    <td className="px-2.5 py-2 whitespace-nowrap font-mono text-gray-800 dark:text-gray-200">{r.ItemCode}</td>
                                                                                    <td className="px-2.5 py-2 text-gray-900 dark:text-white">{r.ItemName}</td>
                                                                                    <td className="px-2.5 py-2 text-gray-600 dark:text-gray-400">{r.Specification}</td>
                                                                                    <td className="px-2.5 py-2 whitespace-nowrap">{r.DcaCode}</td>
                                                                                    <td className="px-2.5 py-2 whitespace-nowrap">{r.SubDCAcode}</td>
                                                                                    <td className="px-2.5 py-2 text-right">{r.Basic}</td>
                                                                                    <td className="px-2.5 py-2 whitespace-nowrap">{r.Units}</td>
                                                                                    <td className="px-2.5 py-2 text-right">{r.Amount}</td>
                                                                                    <td className="px-2.5 py-2 text-right">{r.RaisedQty}</td>
                                                                                    <td className="px-2.5 py-2 text-right">{r.RecievedQty}</td>
                                                                                    <td className="px-2.5 py-2 text-right">{r.PendingVerificationQty}</td>
                                                                                    <td className="px-2.5 py-2 text-right">{r.AvlQuantity}</td>
                                                                                    <td className="px-1.5 py-1.5 w-24">
                                                                                        <input type="number" min="0" step="0.001" className={`${inputCls} text-right`}
                                                                                            value={receivedEdits[r.Rid] ?? ''}
                                                                                            onChange={(e) => setReceivedEdits((p) => ({ ...p, [r.Rid]: e.target.value }))}
                                                                                            onBlur={(e) => handleReceivedBlur(r, e.target.value)} />
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>

                                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                                    <div>
                                                                        <Label required>Upload Pdf File</Label>
                                                                        <div className="relative">
                                                                            <input type="file" accept=".pdf" className={`${inputCls} pl-9`}
                                                                                onChange={(e) => setFile(e.target.files?.[0] || null)} />
                                                                            <Paperclip className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                                                                        </div>
                                                                    </div>
                                                                    <div>
                                                                        <Label required>Date</Label>
                                                                        <CustomDatePicker value={date} onChange={(d) => setDate(formatDateForSP(d))}
                                                                            format="DD-MMM-YYYY" placeholder="Select Date" maxDate={new Date()} />
                                                                    </div>
                                                                    <div>
                                                                        <Label required>Notes</Label>
                                                                        <textarea rows={2} className={inputCls} placeholder="Enter notes…"
                                                                            value={notes} onChange={(e) => setNotes(e.target.value)} />
                                                                    </div>
                                                                </div>

                                                                <div className="flex items-center justify-end gap-3">
                                                                    {uploading && (
                                                                        <div className="flex items-center gap-2 w-48">
                                                                            <div className="flex-1 h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                                                                                <div className="h-full bg-indigo-600 transition-all" style={{ width: `${uploadProgress}%` }} />
                                                                            </div>
                                                                            <span className="text-xs text-gray-500 dark:text-gray-400 w-9 text-right">{uploadProgress}%</span>
                                                                        </div>
                                                                    )}
                                                                    <button onClick={handleSubmit} disabled={loading.submit || uploading}
                                                                        className="flex items-center gap-2 px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors">
                                                                        {(loading.submit || uploading) ? <SpinnerIcon /> : <Send className="w-4 h-4" />}
                                                                        {loading.submit ? 'Submitting…' : uploading ? `Uploading… ${uploadProgress}%` : 'Submit'}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TransferRecieptMulti;
